import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useChatContext } from "stream-chat-react";
import { PanelRight, Settings, Users } from "lucide-react";

import { UserAvatar } from "@/features/chat/components/UserAvatar";
import { VoiceCallButton } from "@/features/chat/components/VoiceCallButton";
import { formatLastSeen } from "@/shared/utils/formatters";

// Channel header with avatar, presence, info pills, and call/panel/settings actions
export const ChatHeader = ({
  onCallClick,
  onSettingsClick,
  rightPanelOpen,
  onToggleRightPanel,
}: {
  onCallClick: () => void;
  onSettingsClick: () => void;
  rightPanelOpen: boolean;
  onToggleRightPanel: () => void;
}) => {
  const { t } = useTranslation();
  const { channel, client } = useChatContext();

  const memberCount = Object.keys(channel?.state.members || {}).length;
  const isGroup = memberCount > 2;

  const otherUser = useMemo(() => {
    if (!channel || isGroup) return null;
    const otherMembers = Object.values(channel.state.members).filter(
      (m) => m.user?.id !== client.userID,
    );
    return otherMembers[0]?.user || null;
  }, [channel, client.userID, isGroup]);

  const displayName = useMemo(() => {
    if (!channel) return "Chat";
    const name = (channel.data as any)?.name;
    if (name) return name as string;
    return otherUser?.name || otherUser?.id || "Chat";
  }, [channel, otherUser]);

  if (!channel) return null;

  const displayCompany =
    (otherUser as any)?.company_name || (otherUser as any)?.company || "";
  const batchNo = (otherUser as any)?.batch_no || "";
  const visaType = (otherUser as any)?.visa_type_descr || "";
  const isOnline = otherUser?.online ?? false;
  const isOtherOfficer =
    ((otherUser as any)?.user_type || "").toLowerCase() === "officer";
  const lastSeen = !isGroup && !isOnline
    ? formatLastSeen((otherUser as any)?.last_active)
    : "";

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200/80 glass">
      <div className="flex items-center gap-3 min-w-0">
        {isGroup ? (
          <div className="w-9 h-9 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
            <Users className="w-[18px] h-[18px] text-indigo-500" />
          </div>
        ) : (
          <div className="relative flex-shrink-0">
            <UserAvatar
              name={otherUser?.name || displayName}
              id={otherUser?.id || channel.id || ""}
              image={otherUser?.image as string | undefined}
              size={36}
            />
            <span
              className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${isOnline ? "bg-green-500" : "bg-slate-300"}`}
              title={isOnline ? t("chat.online") : t("chat.offline")}
            />
          </div>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-slate-900 truncate">
              {displayName}
            </h3>
            {isGroup && (
              <span className="text-xs text-slate-400">({memberCount})</span>
            )}
          </div>
          {lastSeen && (
            <p className="text-[11px] text-slate-400 mt-0.5">{lastSeen}</p>
          )}
          {/* Info pills row for DM chats */}
          {!isGroup && !isOtherOfficer && displayCompany && (
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 truncate max-w-[160px]"
                title={displayCompany}
              >
                {displayCompany}
              </span>
              {batchNo && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                  {t("profile.batch_no")} {batchNo}
                </span>
              )}
              {visaType && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 truncate max-w-[140px]"
                  title={visaType}
                >
                  {visaType}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <VoiceCallButton onClick={onCallClick} isGroup={isGroup} />
        <button
          onClick={onToggleRightPanel}
          className="p-2 hover:bg-slate-100 rounded-full transition-colors hidden md:block"
          title={rightPanelOpen ? t("chat.hide_panel") : t("chat.show_panel")}
        >
          <PanelRight
            className={`w-5 h-5 text-slate-600 transition-transform ${!rightPanelOpen ? "scale-x-[-1]" : ""}`}
          />
        </button>
        {isGroup && (
          <button
            onClick={onSettingsClick}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            title={t("chat.channel_settings")}
          >
            <Settings className="w-5 h-5 text-slate-600" />
          </button>
        )}
      </div>
    </div>
  );
};
